import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { hydrateFromCloud, flushStatEntries } from '@/lib/syncService'
import { restoreActiveState } from '@/lib/restoreState'
import { useAuthStore } from '@/store/authStore'
import { useAppStore } from '@/store/appStore'
import { useEventReminders } from '@/hooks/useEventReminders'
import AppShell from '@/components/shared/AppShell'
import ErrorBoundary from '@/components/shared/ErrorBoundary'
import { initAnalytics, identifyUser, resetUser, capturePageview, analytics } from '@/lib/analytics'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'

initAnalytics()

// ── Lazy-loaded pages (code splitting) ───────────────────────────────────────
const LoginPage          = lazy(() => import('@/pages/LoginPage'))
const DashboardPage      = lazy(() => import('@/pages/DashboardPage'))
const RosterPage         = lazy(() => import('@/pages/RosterPage'))
const LineupPage         = lazy(() => import('@/pages/LineupPage'))
const SchedulePage       = lazy(() => import('@/pages/SchedulePage'))
const SettingsPage       = lazy(() => import('@/pages/SettingsPage'))
const StatsPage          = lazy(() => import('@/pages/StatsPage'))
const PlayerProfilePage  = lazy(() => import('@/pages/PlayerProfilePage'))
const LiveGamePage       = lazy(() => import('@/pages/LiveGamePage'))
const NotFoundPage       = lazy(() => import('@/pages/NotFoundPage'))
const JoinTeamPage       = lazy(() => import('@/pages/JoinTeamPage'))
const DrillLibraryPage   = lazy(() => import('@/pages/DrillLibraryPage'))
const TeamPage           = lazy(() => import('@/pages/TeamPage'))
const PracticePage       = lazy(() => import('@/pages/PracticePage'))
const OpponentsPage           = lazy(() => import('@/pages/OpponentsPage'))
const GameStatsPage           = lazy(() => import('@/pages/GameStatsPage'))
const PublicGameStatsPage     = lazy(() => import('@/pages/PublicGameStatsPage'))
const PublicSeasonStatsPage   = lazy(() => import('@/pages/PublicSeasonStatsPage'))
const PublicRosterPage        = lazy(() => import('@/pages/PublicRosterPage'))
const AvailabilityFormPage    = lazy(() => import('@/pages/AvailabilityFormPage'))

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function FirstSyncScreen() {
  return (
    <div className="min-h-screen bg-[#e2e8f0] dark:bg-[#0f172a] flex flex-col items-center justify-center gap-4 p-8">
      <div className="w-14 h-14 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
      <div className="text-center">
        <p className="text-lg font-bold text-[#0f172a] dark:text-white">Setting up your workspace</p>
        <p className="text-sm text-[#64748b] dark:text-[#94a3b8] mt-1">Downloading your teams and data…</p>
      </div>
    </div>
  )
}

function PageViewTracker() {
  const { pathname } = useLocation()
  useEffect(() => { capturePageview(pathname) }, [pathname])
  return null
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()
  if (isLoading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireEditor({ children }: { children: React.ReactNode }) {
  const role = useAppStore(s => s.activeTeam?.myRole) ?? 'owner'
  if (role === 'statistician' || role === 'viewer') return <Navigate to="/stats" replace />
  return <>{children}</>
}

export default function App() {
  const { setAuth } = useAuthStore()
  const { setOnline, setSyncStatus, setLastSyncedAt, setSyncError, theme, notificationsEnabled, activeSeason } = useAppStore()
  useEventReminders(activeSeason?.id, notificationsEnabled)
  const prevOnline = useRef(navigator.onLine)
  const [readyToRender, setReadyToRender] = useState(false)

  useEffect(() => {
    const root = document.documentElement
    const darkThemes = new Set(['dark', 'high-contrast', 'forest', 'midnight', 'graphite', 'team'])
    const isDark = darkThemes.has(theme) ||
      (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    root.classList.toggle('dark',           isDark)
    root.classList.toggle('hc',             theme === 'high-contrast')
    root.classList.toggle('forest',         theme === 'forest')
    root.classList.toggle('midnight',       theme === 'midnight')
    root.classList.toggle('graphite',       theme === 'graphite')
    root.classList.toggle('team',           theme === 'team')
    root.classList.toggle('forest-light',   theme === 'forest-light')
    root.classList.toggle('midnight-light', theme === 'midnight-light')
    root.classList.toggle('graphite-light', theme === 'graphite-light')
    root.classList.toggle('team-light',     theme === 'team-light')
  }, [theme])

  // Apply per-team brand colors as CSS variables
  const teamColors = useAppStore(s => s.activeTeam?.colors)
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--whi-primary', teamColors?.primary ?? '#2563eb')
    root.style.setProperty('--whi-accent',  teamColors?.secondary ?? '#22c55e')
    root.style.setProperty('--whi-dark-bg', teamColors?.darkShade ?? '#0f172a')
  }, [teamColors])

  async function doSync() {
    setSyncStatus('syncing')
    setSyncError(null)
    // Flush any pending local stat entries before hydrating so they land in Supabase first
    await flushStatEntries()
    const result = await hydrateFromCloud()
    setSyncStatus(result.status)
    if (result.lastSyncedAt) setLastSyncedAt(result.lastSyncedAt)
    if (result.status === 'error' && result.reason) setSyncError(result.reason)
    await restoreActiveState()
    setReadyToRender(true)
  }

  useEffect(() => {
    // Render immediately if we already have local data
    import('@/lib/db').then(({ db }) => {
      db.teams.count().then(count => { if (count > 0) setReadyToRender(true) })
    })

    supabase.auth.getSession().then(({ data }) => {
      setAuth(data.session?.user ?? null, data.session)
      if (data.session?.user) {
        identifyUser(data.session.user.id, { email: data.session.user.email })
        setSentryUser(data.session.user.id, data.session.user.email)
        analytics.appOpened({ theme: useAppStore.getState().theme })
      }
      if (data.session && navigator.onLine) doSync()
      else setReadyToRender(true)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setAuth(session?.user ?? null, session)
      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user.id, { email: session.user.email })
        setSentryUser(session.user.id, session.user.email)
        analytics.appOpened({ theme: useAppStore.getState().theme })
        if (navigator.onLine) doSync()
      }
      if (event === 'SIGNED_OUT') {
        resetUser()
        clearSentryUser()
      }
    })

    const handleOnline = () => {
      setOnline(true)
      if (!prevOnline.current) doSync().then(() => flushStatEntries())
      prevOnline.current = true
    }
    const handleOffline = () => { setOnline(false); prevOnline.current = false }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { user } = useAuthStore()
  const { syncStatus } = useAppStore()
  if (user && !readyToRender && syncStatus === 'syncing') return <FirstSyncScreen />

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <PageViewTracker />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index element={<DashboardPage />} />
              <Route path="practice" element={<RequireEditor><PracticePage /></RequireEditor>} />
              <Route path="gameday"  element={<Navigate to="/schedule" replace />} />
              <Route path="team"     element={<TeamPage />} />
              <Route path="roster"   element={<RequireEditor><RosterPage /></RequireEditor>} />
              <Route path="lineup"   element={<RequireEditor><LineupPage /></RequireEditor>} />
              <Route path="schedule" element={<SchedulePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="stats"    element={<StatsPage />} />
              <Route path="player/:playerId" element={<PlayerProfilePage />} />
              <Route path="drills"     element={<RequireEditor><DrillLibraryPage /></RequireEditor>} />
              <Route path="opponents"  element={<OpponentsPage />} />
            </Route>
            <Route path="/game/:gameId/live"  element={<RequireAuth><ErrorBoundary><LiveGamePage /></ErrorBoundary></RequireAuth>} />
            <Route path="/game/:gameId/stats" element={<RequireAuth><GameStatsPage /></RequireAuth>} />
            <Route path="/p/g/:token"  element={<PublicGameStatsPage />} />
            <Route path="/p/s/:token"  element={<PublicSeasonStatsPage />} />
            <Route path="/p/r/:token"  element={<PublicRosterPage />} />
            <Route path="/p/av/:token" element={<AvailabilityFormPage />} />
            <Route path="/join" element={<JoinTeamPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
